import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/shadcn/command';
import { Label } from '@/components/ui/shadcn/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useControllableOpen } from '@/hooks/use-controllable-open';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { router } from '@inertiajs/react';
import { AlertTriangle, Check, ChevronsUpDown } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

export interface TransferCounts {
    leads_owned: number;
    leads_assigned: number;
    projects: number;
    tasks: number;
    work_orders: number;
    companies: number;
}

interface TargetUser {
    uuid: string;
    first_name: string;
    last_name: string;
}

interface TransferEmployeeModalProps {
    employeeId: string;
    transferCounts: TransferCounts;
    allUsers: TargetUser[];
    trigger?: ReactNode;
    /** Set to drive the modal from the page — see useControllableOpen. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

type EntityKey = keyof TransferCounts;

export default function TransferEmployeeModal({
    employeeId,
    transferCounts,
    allUsers,
    trigger,
    open: openProp,
    onOpenChange,
}: TransferEmployeeModalProps) {
    const { t } = useTranslations();
    const [open, setOpen] = useControllableOpen(openProp, onOpenChange);
    const [comboOpen, setComboOpen] = useState(false);
    const [targetUserId, setTargetUserId] = useState<string>('');
    const [processing, setProcessing] = useState(false);

    const defaultChecked: Record<EntityKey, boolean> = {
        leads_owned: true,
        leads_assigned: true,
        projects: true,
        tasks: true,
        work_orders: true,
        companies: true,
    };

    const [checked, setChecked] = useState<Record<EntityKey, boolean>>(defaultChecked);

    const entities: { key: EntityKey; label: string; translationKey: string }[] = [
        { key: 'leads_owned', label: 'Owned leads', translationKey: 'employees.show.transfer.entity_leads_owned' },
        { key: 'leads_assigned', label: 'Assigned leads', translationKey: 'employees.show.transfer.entity_leads_assigned' },
        { key: 'projects', label: 'Projects', translationKey: 'employees.show.transfer.entity_projects' },
        { key: 'tasks', label: 'Tasks', translationKey: 'employees.show.transfer.entity_tasks' },
        { key: 'work_orders', label: 'Work orders', translationKey: 'employees.show.transfer.entity_work_orders' },
        { key: 'companies', label: 'Company memberships', translationKey: 'employees.show.transfer.entity_companies' },
    ];

    const selectedUser = allUsers.find((u) => u.uuid === targetUserId);

    const handleSubmit = () => {
        if (!targetUserId) {
            toast.error(t('employees.show.transfer.error_no_target', 'Please select a target employee.'));
            return;
        }

        const anyChecked = entities.some(({ key }) => checked[key]);
        if (!anyChecked) {
            toast.error(t('employees.show.transfer.error_no_entities', 'Please select at least one entity type to transfer.'));
            return;
        }

        setProcessing(true);

        router.post(
            route('employees.transfer', { employee: employeeId }),
            {
                target_user_id: targetUserId,
                ...Object.fromEntries(entities.map(({ key }) => [key, checked[key] ? '1' : '0'])),
            },
            {
                onSuccess: () => {
                    setOpen(false);
                    setTargetUserId('');
                    setChecked(defaultChecked);
                },
                onError: () => {
                    toast.error(t('common.error', 'Something went wrong.'));
                },
                onFinish: () => setProcessing(false),
            },
        );
    };

    const handleOpenChange = (next: boolean) => {
        if (!next) {
            setTargetUserId('');
            setChecked(defaultChecked);
            setComboOpen(false);
        }
        setOpen(next);
    };

    return (
        <ResponsiveModal open={open} onOpenChange={handleOpenChange} variant="sheet">
            {trigger ? <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger> : null}
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('employees.show.transfer.title', 'Transfer Employee Data')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('employees.show.transfer.description', 'Move owned and assigned records from this employee to another.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody className="space-y-5">
                    {/* Target user selector */}
                    <div className="space-y-1.5">
                        <Label>{t('employees.show.transfer.select_target', 'Transfer to employee')}</Label>
                        <Popover open={comboOpen} onOpenChange={setComboOpen}>
                            <PopoverTrigger asChild>
                                <Button variant="outline" role="combobox" aria-expanded={comboOpen} className="w-full justify-between font-normal">
                                    {selectedUser
                                        ? `${selectedUser.first_name} ${selectedUser.last_name}`
                                        : t('employees.show.transfer.select_target_placeholder', 'Search employees...')}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                <Command>
                                    <CommandInput placeholder={t('employees.show.transfer.select_target_placeholder', 'Search employees...')} />
                                    <CommandList>
                                        <CommandEmpty>{t('common.no_results', 'No results found.')}</CommandEmpty>
                                        <CommandGroup>
                                            {allUsers.map((u) => (
                                                <CommandItem
                                                    key={u.uuid}
                                                    value={`${u.first_name} ${u.last_name}`}
                                                    onSelect={() => {
                                                        setTargetUserId(u.uuid);
                                                        setComboOpen(false);
                                                    }}
                                                >
                                                    <Check className={cn('mr-2 h-4 w-4', targetUserId === u.uuid ? 'opacity-100' : 'opacity-0')} />
                                                    {u.first_name} {u.last_name}
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>

                    {/* Entity checkboxes */}
                    <div className="space-y-1.5">
                        <Label>{t('employees.show.transfer.select_what', 'Select what to transfer')}</Label>
                        <div className="rounded-md border">
                            {entities.map(({ key, label, translationKey }, i) => {
                                const count = transferCounts[key];
                                return (
                                    <div
                                        key={key}
                                        className={cn('flex items-center justify-between px-3 py-2.5', i < entities.length - 1 && 'border-b')}
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <Checkbox
                                                id={`transfer-${key}`}
                                                checked={checked[key]}
                                                onCheckedChange={(val) => setChecked((prev) => ({ ...prev, [key]: !!val }))}
                                            />
                                            <label htmlFor={`transfer-${key}`} className="cursor-pointer text-sm select-none">
                                                {t(translationKey, label)}
                                            </label>
                                        </div>
                                        <span
                                            className={cn(
                                                'text-xs tabular-nums',
                                                count === 0 ? 'text-muted-foreground' : 'text-foreground font-medium',
                                            )}
                                        >
                                            {count}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Warning */}
                    <div className="bg-warning-subtle text-warning-subtle-foreground flex gap-2 rounded-md px-3 py-2.5 text-sm">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>
                            {t(
                                'employees.show.transfer.warning',
                                'This action cannot be undone. Company memberships will be moved (source employee will be detached).',
                            )}
                        </span>
                    </div>
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={processing}>
                        {t('employees.show.transfer.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleSubmit} disabled={processing || !targetUserId}>
                        {processing ? t('common.saving', 'Saving...') : t('employees.show.transfer.confirm', 'Transfer')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
